import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import logo from '../../../app_icon.svg';
import Modal from '../../../common/components/MessageModal';
import { loginUser } from '../../../api/auth';

import userIcon from '../../../assets/icons/user.svg';
import briefcaseIcon from '../../../assets/icons/briefcase.svg';
import PhoneInput from '../../../common/components/PhoneInput';
import { t } from 'i18next';

import { trackPageView } from '../../../utils/AnalyticsUtils';
import monitoringService from '../../../services/monitoringService';

const Login = () => {
    const navigate = useNavigate();
    const [phone, setPhone] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        trackPageView('login');
    }, []);

    useEffect(() => {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken) {
            navigate('/profile#walletTopUp');
        }
    }, [navigate]);

    const handleBackClick = () => {
        navigate('/');
    };

    const handleCountryChange = (country) => {
        setSelectedCountry(country);
    };

    const handlePhoneChange = (e) => {
        setPhone(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!phone.trim()) {
            setError(t('enter_phone'));
            setShowModal(true);
            return;
        }

        const countryCode = selectedCountry?.code || '+7';
        const sanitizedPhoneNumber = phone.replace(/\D/g, '');
        const formattedPhoneNumber = `${countryCode}${sanitizedPhoneNumber}`;

        setIsLoading(true);
        try {
            const data = await loginUser(formattedPhoneNumber);
            monitoringService.trackLogin(formattedPhoneNumber, false, null).catch(err => {
                console.warn('Failed to track login attempt:', err);
            });
            navigate('/sms-code', {
                state: {
                    taskId: data.links[0].href.split('/').pop(),
                    phoneNumber: formattedPhoneNumber,
                    typeAuth: 'login',
                    email: data.email || null,
                    hasEmailOption: data.links?.some(link => link.name === 'mfaEmailRequest') || false
                }
            });
        } catch (err) {
            const errorMessage = err.message || 'Ошибка сети. Попробуйте позже.';
            monitoringService.trackLogin(formattedPhoneNumber, false, errorMessage).catch(err => {
                console.warn('Failed to track login failure:', err);
            });
            setError(errorMessage);
            setShowModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setError('');
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Section - Decorative */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                {/* Background Image with Overlay */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url('/images/sign.png')" }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#147F26]/95 via-[#0f6820]/90 to-[#0a4d16]/95" />

                {/* Decorative Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-20 w-64 h-64 border-2 border-white rounded-full" />
                    <div className="absolute bottom-32 right-16 w-48 h-48 border-2 border-white rounded-full" />
                    <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white rounded-full" />
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col justify-center px-16 py-12 text-white">
                    <h1 className="text-4xl font-bold mb-4 leading-tight">
                        {t('agrolandTitle') || 'Агроөнімдер нарығына қош келдіңіз'}
                    </h1>
                    <p className="text-lg text-white/80 mb-8">
                        {t('agrolandSubtitle') || 'Сатып алушылар мен сатушыларды байланыстыратын платформа'}
                    </p>

                    {/* Features */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-white/90">{t('feature1') || 'Жылдам жарнамалар'}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-white/90">{t('feature2') || 'Қауіпсіз мәмілелер'}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className="text-white/90">{t('feature3') || 'Тікелей байланыс'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Section - Form */}
            <div className="w-full lg:w-1/2 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 lg:px-12 lg:py-6 flex-shrink-0">
                    <button
                        onClick={handleBackClick}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#147F26] transition-colors"
                    >
                        <div className="w-10 h-10 rounded-full bg-[#147F26]/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </div>
                        <span className="text-sm font-medium hidden sm:inline">{t('back') || 'Артқа'}</span>
                    </button>

                    <img src={logo} alt="Agroland" className="h-10 lg:h-12" />
                </div>

                {/* Form Area */}
                <div className="flex-1 overflow-y-auto px-6 py-6 lg:px-16 lg:py-6 flex items-center">
                    <div className="w-full max-w-md mx-auto">
                        {/* Mobile Logo */}
                        <div className="lg:hidden flex justify-center mb-8">
                            <img src={logo} alt="Agroland" className="h-14" />
                        </div>

                        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                            {t('signIn') || 'Кіру'}
                        </h2>
                        <p className="text-gray-500 mb-8">
                            {t('signInSubtitle') || 'Жарнамаларыңызды басқару үшін кіріңіз'}
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <PhoneInput
                                    label={t('enter_phone')}
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    onCountryChange={handleCountryChange}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-4 bg-[#147F26] hover:bg-[#0f6820] disabled:bg-gray-300 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#147F26]/25"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        {t('loading') || 'Жүктелуде...'}
                                    </>
                                ) : (
                                    <>
                                        {t('next') || 'Әрі қарай'}
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Divider */}
                        <div className="flex items-center gap-4 my-8">
                            <div className="flex-1 h-px bg-gray-200" />
                            <span className="text-sm text-gray-400">{t('or') || 'немесе'}</span>
                            <div className="flex-1 h-px bg-gray-200" />
                        </div>

                        {/* Registration Buttons */}
                        <div className="space-y-3">
                            <button
                                onClick={() => navigate('/register', { state: { userType: 'individual' } })}
                                className="w-full py-4 px-5 border-2 border-gray-200 hover:border-[#147F26] hover:bg-[#147F26]/5 rounded-xl transition-all duration-200 flex items-center justify-between group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#147F26]/10 flex items-center justify-center">
                                        <img src={userIcon} alt="" className="w-5 h-5" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-semibold text-gray-900">{t('forIndividuals') || 'Жеке тұлғалар'}</p>
                                        <p className="text-sm text-gray-500">{t('individualDesc') || 'Жеке шаруашылық'}</p>
                                    </div>
                                </div>
                                <svg className="w-5 h-5 text-gray-400 group-hover:text-[#147F26] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            <button
                                onClick={() => navigate('/register', { state: { userType: 'business' } })}
                                className="w-full py-4 px-5 border-2 border-gray-200 hover:border-[#147F26] hover:bg-[#147F26]/5 rounded-xl transition-all duration-200 flex items-center justify-between group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#147F26]/10 flex items-center justify-center">
                                        <img src={briefcaseIcon} alt="" className="w-5 h-5" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-semibold text-gray-900">{t('forBusinesses') || 'Кәсіпкерлер'}</p>
                                        <p className="text-sm text-gray-500">{t('businessDesc') || 'Заңды тұлғалар'}</p>
                                    </div>
                                </div>
                                <svg className="w-5 h-5 text-gray-400 group-hover:text-[#147F26] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 text-center text-xs text-gray-400 flex-shrink-0 border-t border-gray-100">
                    {t('agreeTerms') || 'Кіру арқылы сіз'}{' '}
                    <a href="/aboutus#terms" className="text-[#147F26] hover:underline">{t('termsOfUse') || 'Пайдалану шарттарына'}</a>
                    {' '}{t('and') || 'және'}{' '}
                    <a href="/aboutus#policy" className="text-[#147F26] hover:underline">{t('privacyPolicy') || 'Құпиялылық саясатына'}</a>
                    {' '}{t('agree') || 'келісесіз'}
                </div>
            </div>

            <Modal show={showModal} message={error} onClose={handleCloseModal} />
        </div>
    );
};

export default Login;
